import { Component, Input, OnChanges } from "@angular/core";
import { DEFAULT_CURRENCY, ModelDetails, ModelInsights, Organisation, Project, canDeleteModel, createInsightsFilter, getOrganisationModelList, getTotalModelCost, getUnsortedModelList } from "../../common/organisation";
import { NodeService } from "../../model-services/node.service";
import { USER_ROLE, getUserRoleName } from "../../common/user";
import { FUNCTION, FunctionService } from "../../model-services/function.service";
import { ModelService } from "./model-service";
import { CURRENT_STATE_PROJECT_KEY, Linqset, NODE_SUPERTYPE } from "../../common/linqset-model";
import { createLinqset } from "../../common/model-processing";
import { Column } from "../../ui-components/table/table-component";
import { NodeStyleSettings } from "../../ui-components/svgs/node-svgs";
import { OrganisationService } from "../organisation.service";
import { Router } from "@angular/router";

const HIDDEN_COLUMNS_KEY = "linq.modelTable.hiddenColumns"
const DEFAULT_HIDDEN_COLUMNS = ['createdDate', 'access', 'orgAccess', 'introduction']
const FIXED_COLUMNS = ['settings', 'name']     // always shown, not offered in the column picker

class ModelRowData {
    public _key:string
    public name:string
    public createdDate:Date
    public updatedDate:Date
    public reviewDate:Date
    public orgAccess:string
    public functionName:string
    public access:string
    public tags:string
    public hours:number
    public cost:number
    public revenue:number
    public canEdit:boolean
    public canDelete:boolean
    public introduction:boolean = true

    constructor(public model:ModelDetails, public owner:string, public settings:NodeStyleSettings, insights:ModelInsights, tagMap:any) {
        this._key = model._key
        this.name = model.name
        this.createdDate = model.createdDate
        this.updatedDate = (model.changes&&insights)?model.changes.find(c => c.projectKey === insights.projectKey)?.date:undefined
        this.reviewDate = model.reviewDate
        this.orgAccess = model.useOrganisationAccess?'Organisation':'Model users'
        this.hours = insights?insights.superTypes[NODE_SUPERTYPE.ACTION].hours:0
        this.cost = insights?getTotalModelCost(insights):0
        this.revenue = insights?insights.outputRevenue:0
        this.functionName = model.function && model.function.toString()
        this.access = getUserRoleName(model.role)
        this.tags = this.getTagList(tagMap)
        this.canEdit = model.role && model.role === USER_ROLE.ADMIN
    }
    
    getTagList(tagMap:any) {       
        const tags = this.model.tagKeys?this.model.tagKeys.map(t => {
            return tagMap[t]
        }):[]
        return tags.filter(t => t).join(", ")
    }

}

@Component({
    selector: 'app-model-table',
    templateUrl: './model-table.html',
    styleUrls: ['./model-table.css'],
    standalone: false
})
export class ModelTableComponent implements OnChanges {
    @Input() organisation:Organisation
    @Input() organisationLinqset:Linqset
    @Input() project:Project
    @Input() compact:boolean = false     // simple list for small screens instead of the full table

    allColumns:Column[]
    columns:Column[]
    hiddenColumns:string[]
    data:ModelRowData[]
    filterChangedModels:boolean = false
    modelList:ModelDetails[] = []

    constructor(private nodeService:NodeService, private functionService:FunctionService, private modelService:ModelService, private organisationService:OrganisationService, private router:Router) {
        this.allColumns = [
            new Column('settings', '', 'node-svg'),
            new Column('name', 'Name', 'string'),
            new Column('owner', 'Owner', 'string'),
            new Column('functionName', 'Function', 'string'),
            new Column('createdDate', 'Created', 'date'),
            new Column('updatedDate', 'Updated', 'date'),
            new Column('reviewDate', 'Review', 'date'),
            new Column('hours', 'Hours', 'number'),
            new Column('cost', 'Cost', 'currency'),
            new Column('revenue', 'Revenue', 'currency'),
            new Column('access', 'Your access', 'string'),
            new Column('orgAccess', 'Access from', 'string'),
            new Column('tags', 'Tags', 'string'),
            new Column('canEdit', 'Edit', 'icon-button', 'Edit model details', 'accent', 'edit'),
            new Column('canDelete', 'Delete', 'icon-button', 'Archive or delete this model', 'warn', 'delete'),
            new Column('introduction', 'Intro', 'icon-button', 'Open the guided introduction to this model', 'accent', 'tour')
        ]
        this.hiddenColumns = this.loadHiddenColumns()
        this.updateColumns()
    }

    get optionalColumns() {
        return this.allColumns.filter(c => FIXED_COLUMNS.indexOf(c.name) < 0)
    }

    isColumnVisible(column:Column) {
        return this.hiddenColumns.indexOf(column.name) < 0
    }

    toggleColumn(column:Column) {
        this.hiddenColumns = this.isColumnVisible(column)?[...this.hiddenColumns, column.name]:this.hiddenColumns.filter(c => c !== column.name)
        this.saveHiddenColumns()
        this.updateColumns()
    }

    resetColumns() {
        this.hiddenColumns = [...DEFAULT_HIDDEN_COLUMNS]
        this.saveHiddenColumns()
        this.updateColumns()
    }

    get currencyCode() {
        return this.organisation?.currencyCode||DEFAULT_CURRENCY
    }

    private updateColumns() {
        this.columns = this.allColumns.filter(c => this.isColumnVisible(c))
    }

    // column choice is a per-browser convenience, so it lives in local storage rather than the user's saved preferences
    private loadHiddenColumns():string[] {
        try {
            const stored = localStorage.getItem(HIDDEN_COLUMNS_KEY)
            const parsed = stored?JSON.parse(stored):undefined
            if (Array.isArray(parsed)) return parsed
        }catch(err) {
            // storage unavailable or corrupt, fall back to defaults
        }
        return [...DEFAULT_HIDDEN_COLUMNS]
    }

    private saveHiddenColumns() {
        try {
            localStorage.setItem(HIDDEN_COLUMNS_KEY, JSON.stringify(this.hiddenColumns))
        }catch(err) {
            // storage unavailable; the choice just won't persist
        }
    }

    ngOnChanges(changes) {
        if ((changes["organisation"] || changes["project"] || changes["organisationLinqset"]) && this.organisation) {
            this.modelList = getOrganisationModelList(this.organisation, this.organisationLinqset, this.project)
            if (!this.project) this.filterChangedModels = false
            this.refreshTable()
        }
    }

    // keeps the changed-models filter as it is, so a reload doesn't quietly undo it
    refreshTable(filter:boolean = this.filterChangedModels) {
        const tagMap = this.organisation.modelTags?this.organisation.modelTags.tags.reduce((m, t) => {
            m[t._key] = t.name
            return m
        }, {}):{}

        const users = this.organisationService.users || []

        const models = filter && this.project?this.modelList.filter(m => m.projects && m.projects.indexOf(this.project._key)>=0):getUnsortedModelList(this.organisation, this.project)
        this.data = [...models].sort((a,b) => (a.name || '').localeCompare(b.name || '', undefined, {sensitivity:'base'}))
            .map(m => {
                const settings = this.nodeService.createModelNodeSettings(m, this.project?._key)
                const owner = (m.owner && users.find(u => u._key === m.owner)?.name) || ""
                const insights = m.insights?.find(i => i.projectKey === (this.project?._key||CURRENT_STATE_PROJECT_KEY))
                const row = new ModelRowData(m, owner, settings, insights, tagMap)
                row.canDelete = canDeleteModel(this.organisation, m, this.project)
                return row
            })
    }

    toggleChangedModelsFilter() {
        this.refreshTable()
    }

    getComparisonKey() {return this.project?CURRENT_STATE_PROJECT_KEY:undefined}

    hasAccess(row:ModelRowData) {
        return row.model.role?true:false
    }

    async handleRowEvent(rowEvent:{row:ModelRowData, column?:Column}) {
        if (rowEvent.row) {
            if (!rowEvent.column) {
                this.openModel(rowEvent.row)
            }else if (rowEvent.column.name === "canDelete") {
                this.deleteModel(rowEvent.row)
            }else if (rowEvent.column.name === "canEdit") {
                this.updateModelDetails(rowEvent.row)
            }else if (rowEvent.column.name === "introduction") {
                this.router.navigateByUrl(`introduction/organisations/${this.organisation._key}/projects/${this.project?._key||CURRENT_STATE_PROJECT_KEY}/models/${rowEvent.row.model._key}`)
            }
        }
    }

    createLinset(row:ModelRowData) {
        const filter = createInsightsFilter(this.project?._key)
        filter.modelKeys = [row.model._key]
        return createLinqset(this.organisation, filter)
    }

    updateModelDetails(row:ModelRowData) {
        this.functionService.executeFunction(FUNCTION.EDIT_MODEL_DETAILS, undefined, this.createLinset(row))
    }

    openModel(row:ModelRowData) {
        this.modelService.editModel(this.organisation, row.model, this.project?._key||CURRENT_STATE_PROJECT_KEY)
    }

    async deleteModel(row:ModelRowData) {
        if (canDeleteModel(this.organisation, row.model, this.project))
            await this.functionService.executeFunction(FUNCTION.DELETE_MODEL, undefined, this.createLinset(row))
    }
}
